import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type RecursoDoFluxo } from '@/lib/api';

/**
 * Fluxo de criação liberado pelo administrador.
 *
 * Cada passo do assistente e cada recurso personalizável tem uma chave
 * (`passo:tema`, `recurso:fundos-prontos`...). O que não estiver liberado não
 * aparece para o cliente — nem no assistente, nem nas telas de configuração.
 *
 * Falha de rede não pode esconder o produto de ninguém: sem resposta, tudo é
 * tratado como liberado (o comportamento antigo).
 */
interface ContextoRecursos {
  recursos: RecursoDoFluxo[];
  carregando: boolean;
  /** O item está liberado? Sem lista carregada, devolve `true`. */
  liberado: (chave: string) => boolean;
  recarregar: () => Promise<void>;
}

const Contexto = createContext<ContextoRecursos | null>(null);

export function ProvedorRecursos({ children }: { children: ReactNode }) {
  const [recursos, setRecursos] = useState<RecursoDoFluxo[]>([]);
  const [carregando, setCarregando] = useState(true);

  const recarregar = useCallback(async () => {
    try {
      const resposta = await api.listarRecursos();
      setRecursos(resposta.recursos);
    } catch {
      setRecursos([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const valor = useMemo<ContextoRecursos>(() => {
    const liberado = (chave: string) => {
      if (recursos.length === 0) return true;
      const item = recursos.find((recurso) => recurso.chave === chave);
      return item ? item.liberado : true;
    };

    return { recursos, carregando, liberado, recarregar };
  }, [recursos, carregando, recarregar]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useRecursos(): ContextoRecursos {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useRecursos precisa estar dentro de <ProvedorRecursos>.');
  return contexto;
}
